// Capacitor Live Update — 자체 호스팅 번들 OTA 적용
//
// 서버가 배포할 때마다 scripts/build_live_update_bundle.sh가 만들어 두는
// /live-update/latest.json 을 직접 조회해 새 번들을 내려받는다.
// 플러그인의 sync()는 Capawesome 클라우드 전용이라 쓰지 않는다.
//
// 흐름: latest.json 조회 → 현재 번들과 비교 → downloadBundle() → setNextBundle() → reload()
//
// 롤백 안전장치는 capacitor.config.ts의 readyTimeout(10초)이 담당한다.
// 새 번들이 켜진 뒤 confirmBundleReady()가 호출되지 않으면 기본 번들로 되돌아간다.

import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { LiveUpdate } from '@capawesome/capacitor-live-update';

/** 앱이 로드된 시각 — 즉시 재시작해도 되는 시점인지 판단하는 기준 */
const APP_STARTED_AT = Date.now();

/**
 * 앱 구동 후 이 시간 안에 새 번들 다운로드가 끝나면 곧바로 reload()로 적용한다.
 * 이 시간을 넘겼다면 사용자가 이미 앱을 쓰고 있을 가능성이 높으므로,
 * setNextBundle()까지만 해두고 다음 실행 때 자연스럽게 적용되도록 미룬다.
 */
const IMMEDIATE_RELOAD_WINDOW_MS = 15_000;

const MANIFEST_TIMEOUT_MS = 10_000;

/** 프로덕션 호스트 — 네이티브 앱은 항상 운영 서버를 바라본다 */
const DEFAULT_HOST = 'https://sudamath.xyz';

interface BundleManifest {
  bundleId: string;
  url: string;
  checksum: string;
}

/** 네이티브(Android/iOS)에서만 동작 — 웹에서는 모든 함수가 no-op */
const isNative = (): boolean => Capacitor.isNativePlatform();

function manifestUrl(): string {
  const base = (
    process.env.REACT_APP_LIVE_UPDATE_BASE_URL ??
    process.env.REACT_APP_API_BASE_URL ??
    DEFAULT_HOST
  ).replace(/\/+$/, '');
  return `${base}/live-update/latest.json`;
}

/**
 * 현재 실행 중인 번들이 정상 구동됐음을 플러그인에 알린다.
 *
 * capacitor.config.ts의 readyTimeout 안에 호출되지 않으면 플러그인이 이 번들을
 * 실패로 간주하고 기본 번들로 롤백한다. 따라서 앱 시작 직후 가장 먼저 호출해야 한다.
 */
export async function confirmBundleReady(): Promise<void> {
  if (!isNative()) return;
  try {
    await LiveUpdate.ready();
  } catch (err) {
    console.warn('[LiveUpdate] ready() 호출 실패', err);
  }
}

/**
 * latest.json을 조회해 새 번들이 있으면 내려받아 적용한다.
 *
 * 즉시 적용된 경우 reload()가 앱을 재시작하므로 이 함수는 반환되지 않을 수 있다.
 * 모든 실패는 삼킨다 — 업데이트가 실패해도 앱 사용은 막히면 안 된다.
 */
export async function checkForUpdate(): Promise<void> {
  if (!isNative()) return;

  try {
    const manifest = await fetchManifest();
    if (!manifest) return;

    const { bundleId: currentBundleId } = await LiveUpdate.getCurrentBundle();
    if (currentBundleId === manifest.bundleId) return; // 이미 최신

    // 이전에 받아둔 번들이면 다시 내려받지 않는다
    const { bundleIds } = await LiveUpdate.getBundles();
    if (!bundleIds.includes(manifest.bundleId)) {
      await LiveUpdate.downloadBundle({
        bundleId: manifest.bundleId,
        url: manifest.url,
        checksum: manifest.checksum, // 서버가 계산한 zip의 SHA-256 (무결성 검증)
      });
    }

    await LiveUpdate.setNextBundle({ bundleId: manifest.bundleId });

    if (Date.now() - APP_STARTED_AT < IMMEDIATE_RELOAD_WINDOW_MS) {
      await LiveUpdate.reload();
    }
    // 창을 넘겼으면 적용하지 않고 둔다 — 다음 앱 실행 때 새 번들로 켜진다.
  } catch (err) {
    console.warn('[LiveUpdate] 업데이트 확인 실패', err);
  }
}

async function fetchManifest(): Promise<BundleManifest | null> {
  // WebView의 fetch()를 쓰면 안 된다 — 네이티브 앱의 오리진은 https://localhost 라서
  // sudamath.xyz 요청이 교차 출처가 되고, /live-update/ 에는 CORS 헤더가 없어
  // "blocked by CORS policy"로 실패한다. CapacitorHttp는 네이티브 계층에서
  // 요청하므로 WebView의 CORS 정책을 받지 않는다.
  const res = await CapacitorHttp.get({
    // 캐시된 매니페스트를 잡으면 업데이트를 영영 못 받으므로 캐시를 우회한다
    url: `${manifestUrl()}?t=${Date.now()}`,
    headers: { 'Cache-Control': 'no-cache' },
    readTimeout: MANIFEST_TIMEOUT_MS,
    connectTimeout: MANIFEST_TIMEOUT_MS,
  });

  if (res.status < 200 || res.status >= 300) return null;

  // CapacitorHttp는 Content-Type이 JSON이면 파싱된 객체를, 아니면 문자열을 준다
  const raw = typeof res.data === 'string' ? safeJsonParse(res.data) : res.data;
  const data = (raw ?? {}) as Partial<BundleManifest>;
  if (!data.bundleId || !data.url || !data.checksum) return null;

  return data as BundleManifest;
}

function safeJsonParse(text: string): unknown | null {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}
